
import { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  Plus,
  FileText,
  Loader2,
  Edit3,
  Trash2,
  Copy,
  Eye,
  EyeOff,
  ExternalLink,
  MoreVertical,
  X,
  Check,
} from 'lucide-react'

import Card from '../../components/ui/Card'
import Badge from '../../components/ui/Badge'
import Button from '../../components/ui/Button'
import api from '../../lib/axios'
import { useAuthStore } from '../../stores/authStore'

function getEditPath(page) {
  switch (page?.type) {
    case 'menu':
      return `/app/menus/${page.id}/edit`
    case 'google_review':
      return `/app/reviews/${page.id}/edit`
    default:
      return `/app/pages/${page.id}/edit`
  }
}

function getPublicPath(page) {
  if (!page?.slug) return ''

  switch (page.type) {
    case 'menu':
      return `/menu/${page.slug}`
    case 'google_review':
      return `/avis/${page.slug}`
    default:
      return `/${page.slug}`
  }
}

function TypeLabel({ type }) {
  if (type === 'vcard') return '📇 Carte'
  if (type === 'bio') return '🔗 Bio'
  if (type === 'menu') return '🍽️ Menu'
  if (type === 'google_review') return '⭐ Avis Google'
  return type
}

export default function MyPages() {
  const navigate = useNavigate()
  const isAdmin = useAuthStore((state) => state.isAdmin())

  const [pages, setPages] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [menuOpen, setMenuOpen] = useState(null)
  const [deleteConfirm, setDeleteConfirm] = useState(null)

  const loadPages = useCallback(async () => {
    try {
      setError('')
      const { data } = await api.get('/pages')
      setPages(data?.data || [])
    } catch (err) {
      console.error('Erreur chargement pages:', err)
      setError('Impossible de charger vos pages.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadPages()
  }, [loadPages])

  const handleEdit = (page) => {
    navigate(getEditPath(page))
  }

  const handleView = (page) => {
    const path = getPublicPath(page)
    if (!path) return
    window.open(path, '_blank', 'noopener,noreferrer')
  }

  const handleTogglePublish = async (page) => {
    try {
      setError('')
      const action = page.is_published ? 'unpublish' : 'publish'

      await api.post(`/pages/${page.id}/${action}`)

      setPages((prev) =>
        prev.map((p) =>
          p.id === page.id
            ? { ...p, is_published: !p.is_published }
            : p
        )
      )

      setSuccess(page.is_published ? 'Page dépubliée' : 'Page publiée !')
      setMenuOpen(null)
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Erreur changement de statut:', err)
      setError('Erreur lors du changement de statut.')
    }
  }

  const handleDuplicate = async (page) => {
    try {
      setError('')

      const { data } = await api.post(`/pages/${page.id}/duplicate`)

      if (data?.data) {
        setPages((prev) => [data.data, ...prev])
      }

      setSuccess('Page dupliquée avec succès !')
      setMenuOpen(null)
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Erreur duplication page:', err)
      setError('Erreur lors de la duplication.')
    }
  }

  const handleDelete = async (page) => {
    try {
      setError('')

      await api.delete(`/pages/${page.id}`)

      setPages((prev) => prev.filter((p) => p.id !== page.id))

      setSuccess('Page supprimée')
      setDeleteConfirm(null)
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      console.error('Erreur suppression page:', err)
      setError('Erreur lors de la suppression.')
    }
  }

  const handleCopyUrl = async (page) => {
    try {
      const url = `${window.location.origin}${getPublicPath(page)}`
      await navigator.clipboard.writeText(url)

      setSuccess('URL copiée !')
      setMenuOpen(null)
      setTimeout(() => setSuccess(''), 2000)
    } catch (err) {
      console.error('Erreur copie URL:', err)
      setError('Impossible de copier le lien.')
    }
  }

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-navy">
            Mes Pages
          </h1>

          <p className="text-sm text-gray-500 mt-1">
            Gérez vos pages publiques (carte de visite, bio, menu, avis Google)
          </p>
        </div>

        {/* Création réservée à Admin */}
        {isAdmin && (
          <Link to="/app/pages/new">
            <Button icon={Plus}>Créer une page</Button>
          </Link>
        )}
      </div>

      {/* ALERTES */}
      {error && (
        <div
          role="alert"
          className="p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-center justify-between gap-3"
        >
          <span>{error}</span>
          <button
            type="button"
            aria-label="Fermer le message d'erreur"
            onClick={() => setError('')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {success && (
        <div
          role="status"
          className="p-3 bg-teal-50 border border-teal-100 rounded-xl text-sm text-teal-700 flex items-center justify-between gap-3"
        >
          <span className="flex items-center gap-2">
            <Check className="w-4 h-4" />
            {success}
          </span>
          <button
            type="button"
            aria-label="Fermer le message de succès"
            onClick={() => setSuccess('')}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* CONTENU */}
      {loading ? (
        <Card className="p-12 flex items-center justify-center">
          <Loader2 className="w-6 h-6 text-teal animate-spin" />
        </Card>
      ) : pages.length === 0 ? (
        <Card className="p-12 text-center">
          <div className="w-16 h-16 bg-graysoft rounded-2xl mx-auto flex items-center justify-center mb-4">
            <FileText className="w-8 h-8 text-gray-400" />
          </div>

          <h3 className="font-semibold text-navy mb-2">
            Aucune page pour le moment
          </h3>

          <p className="text-sm text-gray-500 mb-6">
            {isAdmin
              ? 'Créez votre première page pour commencer.'
              : 'Vous n’avez pas encore de page.'}
          </p>

          {isAdmin && (
            <Link to="/app/pages/new">
              <Button icon={Plus}>
                Créer ma première page
              </Button>
            </Link>
          )}
        </Card>
      ) : (
        <div className="grid gap-3">
          {pages.map((page) => {
            const isSpecial =
              page.type === 'menu' || page.type === 'google_review'

            return (
              <Card
                key={page.id}
                className="p-4 relative"
                hover
              >
                <div className="flex items-center gap-4">
                  {/* AVATAR */}
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-navy to-navy-700 flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {page.avatar ? (
                      <img
                        src={page.avatar}
                        alt={page.title || 'Page'}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-teal font-bold text-lg">
                        {page.title?.charAt(0)?.toUpperCase() || 'P'}
                      </span>
                    )}
                  </div>

                  {/* INFOS */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <h3 className="font-semibold text-navy truncate">
                        {page.title}
                      </h3>

                      <Badge
                        variant={page.is_published ? 'success' : 'warning'}
                        dot
                      >
                        {page.is_published ? 'Publiée' : 'Brouillon'}
                      </Badge>

                      {page.type && (
                        <Badge variant="gray" className="text-[10px]">
                          {TypeLabel({ type: page.type })}
                        </Badge>
                      )}
                    </div>

                    <p className="text-sm text-gray-500 truncate">
                      tapio.link{getPublicPath(page)}
                    </p>

                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500 flex-wrap">
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {page.views || 0} vues
                      </span>

                      {!isSpecial && Array.isArray(page.links) && (
                        <span className="flex items-center gap-1">
                          🔗 {page.links.length} liens
                        </span>
                      )}

                      {Array.isArray(page.nfc_cards) &&
                        page.nfc_cards.length > 0 && (
                          <span className="flex items-center gap-1">
                            💳 {page.nfc_cards.length} carte(s)
                          </span>
                        )}
                    </div>
                  </div>

                  {/* ACTIONS */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleView(page)}
                      title="Voir la page"
                      disabled={!page.slug}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      className="text-xs"
                      onClick={() => handleEdit(page)}
                    >
                      <Edit3 className="w-3 h-3" />
                      Éditer
                    </Button>

                    <div className="relative">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setMenuOpen(menuOpen === page.id ? null : page.id)
                        }
                        title="Plus d'actions"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Button>

                      {menuOpen === page.id && (
                        <>
                          <div
                            className="fixed inset-0 z-30"
                            onClick={() => setMenuOpen(null)}
                          />

                          <div className="absolute top-full right-0 mt-1 w-48 bg-white rounded-xl shadow-dropdown border border-gray-100 py-1 z-40">
                            <button
                              type="button"
                              onClick={() => handleTogglePublish(page)}
                              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-graysoft transition-colors"
                            >
                              {page.is_published ? (
                                <>
                                  <EyeOff className="w-4 h-4 text-gray-500" />
                                  Dépublier
                                </>
                              ) : (
                                <>
                                  <Eye className="w-4 h-4 text-teal" />
                                  Publier
                                </>
                              )}
                            </button>

                            <button
                              type="button"
                              onClick={() => handleCopyUrl(page)}
                              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-graysoft transition-colors"
                            >
                              <Copy className="w-4 h-4 text-gray-500" />
                              Copier l'URL
                            </button>

                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => handleDuplicate(page)}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-graysoft transition-colors"
                              >
                                <FileText className="w-4 h-4 text-gray-500" />
                                Dupliquer
                              </button>
                            )}

                            <div className="border-t border-gray-100 my-1" />

                            {isAdmin && (
                              <button
                                type="button"
                                onClick={() => {
                                  setDeleteConfirm(page)
                                  setMenuOpen(null)
                                }}
                                className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                                Supprimer
                              </button>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      )}

      {/* MODAL SUPPRESSION — ADMIN */}
      {isAdmin && deleteConfirm && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setDeleteConfirm(null)}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-page-title"
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-4">
              <Trash2 className="w-6 h-6 text-red-500" />
            </div>

            <h2
              id="delete-page-title"
              className="font-bold text-navy text-lg mb-2"
            >
              Supprimer cette page ?
            </h2>

            <p className="text-sm text-gray-500 mb-6 leading-relaxed">
              Êtes-vous sûr de vouloir supprimer{' '}
              <strong className="text-navy">
                {deleteConfirm.title}
              </strong>
              ? Cette action est irréversible.
            </p>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setDeleteConfirm(null)}
              >
                Annuler
              </Button>

              <button
                type="button"
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl transition-colors"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
